// Realtime (ARCHITECTURE section 6): one shared WebSocket to /api/ws.
// Client ops: {"op":"sub","channel"}, {"op":"unsub","channel"}, {"op":"ping"}, {"op":"typing","conversationId"}.
// Server frames: {"channel","event","data"}. Subscriptions are ref counted per channel.
import { useEffect, useRef } from 'react'
import { create } from 'zustand'
import { onUnauthorized } from './api'

export type RealtimeHandler = (event: string, data: unknown, channel: string) => void
export type ConnectionState = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'offline'

type WsCtor = new (url: string) => WebSocket

export type RealtimeOptions = {
  url?: () => string
  WebSocketImpl?: WsCtor
  pingMs?: number
  /** Close the socket this long after the last unsubscribe. */
  idleCloseMs?: number
  onState?: (state: ConnectionState) => void
}

const AUTH_CLOSE_CODES = new Set([1008, 4001, 4003, 4401, 4403])

/** ws(s)://<api host>/api/ws when VITE_API_URL is absolute, else the page origin (Vite proxies /api). */
export function defaultSocketUrl(): string {
  const base = (import.meta.env.VITE_API_URL as string | undefined) ?? ''
  if (/^https?:\/\//.test(base)) return base.replace(/^http/, 'ws').replace(/\/$/, '') + '/api/ws'
  const loc = typeof location !== 'undefined' ? location : { protocol: 'http:', host: 'localhost' }
  return `${loc.protocol === 'https:' ? 'wss' : 'ws'}://${loc.host}/api/ws`
}

/** Reconnect delay with full jitter (CAP-08): anywhere up to 1 s, 2 s, 4 s ... capped at 30 s, at least 0.5 s,
 * so thousands of clients that lost their socket together do not all come back in the same instant. */
export function backoffDelay(attempt: number, random: () => number = Math.random): number {
  const cap = Math.min(30_000, 1000 * 2 ** Math.max(0, attempt))
  return Math.max(500, Math.round(random() * cap))
}

/** Close codes of a server restart, a dropped network or a busy server (1013): many clients at once. */
const SPREAD_CLOSE_CODES = new Set([1001, 1006, 1012, 1013])

/** First retry after such a close: spread evenly over 1 to 15 s so a deploy does not get every member back
 * (each reconnect re authorizes every channel and refetches the session) inside one second. */
export function spreadDelay(random: () => number = Math.random): number {
  return Math.round(1000 + random() * 14_000)
}

export class RealtimeClient {
  private ws: WebSocket | null = null
  private readonly subs = new Map<string, Set<RealtimeHandler>>()
  private readonly anyHandlers = new Set<RealtimeHandler>()
  private readonly reconnectHandlers = new Set<() => void>()
  private readonly authLostHandlers = new Set<() => void>()
  private authed = false
  private attempt = 0
  private everOpened = false
  private pingTimer: ReturnType<typeof setInterval> | null = null
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private idleTimer: ReturnType<typeof setTimeout> | null = null
  private readonly opts: Required<Omit<RealtimeOptions, 'WebSocketImpl' | 'onState'>> &
    Pick<RealtimeOptions, 'WebSocketImpl' | 'onState'>
  state: ConnectionState = 'idle'

  constructor(opts: RealtimeOptions = {}) {
    this.opts = { url: defaultSocketUrl, pingMs: 25_000, idleCloseMs: 10_000, ...opts }
  }

  /** Number of handlers on a channel (the ref count). */
  refCount(channel: string): number {
    return this.subs.get(channel)?.size ?? 0
  }

  get channels(): string[] {
    return [...this.subs.keys()]
  }

  /** Only logged in sessions open a socket (the server authenticates with the session cookie). */
  setAuth(loggedIn: boolean): void {
    if (loggedIn === this.authed) return
    this.authed = loggedIn
    if (loggedIn) this.ensureConnected()
    else this.close('idle')
  }

  subscribe(channel: string, handler: RealtimeHandler): () => void {
    let set = this.subs.get(channel)
    if (!set) {
      set = new Set()
      this.subs.set(channel, set)
    }
    const first = set.size === 0
    set.add(handler)
    if (this.idleTimer) {
      clearTimeout(this.idleTimer)
      this.idleTimer = null
    }
    if (first) this.send({ op: 'sub', channel })
    this.ensureConnected()
    let active = true
    return () => {
      if (!active) return
      active = false
      const current = this.subs.get(channel)
      if (!current) return
      current.delete(handler)
      if (current.size === 0) {
        this.subs.delete(channel)
        this.send({ op: 'unsub', channel })
        if (this.subs.size === 0) this.scheduleIdleClose()
      }
    }
  }

  /** Receives every event on every channel (global handlers such as unread badges). */
  onAny(handler: RealtimeHandler): () => void {
    this.anyHandlers.add(handler)
    return () => this.anyHandlers.delete(handler)
  }

  /** Called when the server closes the socket because the session ended (log out everywhere, a password reset,
   * suspension): the app re checks the session so this device stops showing the member view (E2E-J2-02). */
  onAuthLost(handler: () => void): () => void {
    this.authLostHandlers.add(handler)
    return () => this.authLostHandlers.delete(handler)
  }

  /** Called after a dropped socket comes back (refetch whatever may have been missed). */
  onReconnect(handler: () => void): () => void {
    this.reconnectHandlers.add(handler)
    return () => this.reconnectHandlers.delete(handler)
  }

  typing(conversationId: string): void {
    this.send({ op: 'typing', conversationId })
  }

  /** Retry immediately (browser came back online or the tab became visible). */
  reconnectNow(): void {
    if (!this.authed || this.subs.size === 0) return
    if (this.ws && (this.ws.readyState === 0 || this.ws.readyState === 1)) return
    this.attempt = 0
    this.clearRetry()
    this.connect()
  }

  private ensureConnected(): void {
    if (!this.authed || this.subs.size === 0 || this.ws || this.retryTimer) return
    this.connect()
  }

  private connect(): void {
    const Impl = this.opts.WebSocketImpl ?? (globalThis.WebSocket as WsCtor | undefined)
    if (!Impl) return
    this.setState(this.everOpened ? 'reconnecting' : 'connecting')
    let ws: WebSocket
    try {
      ws = new Impl(this.opts.url())
    } catch {
      this.scheduleReconnect()
      return
    }
    this.ws = ws
    ws.onopen = () => {
      if (this.ws !== ws) return
      const wasReconnect = this.everOpened
      this.everOpened = true
      this.attempt = 0
      this.setState('open')
      for (const channel of this.subs.keys()) this.rawSend(ws, { op: 'sub', channel })
      this.startPing()
      if (wasReconnect) this.reconnectHandlers.forEach((fn) => safeCall(fn))
    }
    ws.onmessage = (ev: MessageEvent) => {
      if (this.ws !== ws) return
      this.dispatch(ev.data)
    }
    ws.onclose = (ev: CloseEvent) => {
      if (this.ws !== ws) return
      this.ws = null
      this.stopPing()
      if (AUTH_CLOSE_CODES.has(ev.code)) {
        this.authed = false
        this.setState('idle')
        this.authLostHandlers.forEach((fn) => safeCall(fn))
        return
      }
      this.scheduleReconnect(ev.code)
    }
    ws.onerror = () => {
      /* onclose follows and schedules the retry */
    }
  }

  private dispatch(raw: unknown): void {
    if (typeof raw !== 'string') return
    let msg: { channel?: unknown; event?: unknown; data?: unknown }
    try {
      msg = JSON.parse(raw)
    } catch {
      return
    }
    if (typeof msg.channel !== 'string' || typeof msg.event !== 'string') return
    const { channel, event, data } = msg as { channel: string; event: string; data: unknown }
    const handlers = this.subs.get(channel)
    if (handlers) [...handlers].forEach((h) => safeCall(() => h(event, data, channel)))
    ;[...this.anyHandlers].forEach((h) => safeCall(() => h(event, data, channel)))
  }

  private scheduleReconnect(closeCode?: number): void {
    if (!this.authed || this.subs.size === 0) {
      this.setState('idle')
      return
    }
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false
    this.setState(offline ? 'offline' : 'reconnecting')
    this.clearRetry()
    const spread = this.attempt === 0 && closeCode !== undefined && SPREAD_CLOSE_CODES.has(closeCode)
    const delay = spread ? spreadDelay() : backoffDelay(this.attempt)
    this.attempt++
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null
      this.ensureConnected()
    }, delay)
  }

  private scheduleIdleClose(): void {
    if (this.idleTimer) clearTimeout(this.idleTimer)
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null
      if (this.subs.size === 0) this.close('idle')
    }, this.opts.idleCloseMs)
  }

  private close(state: ConnectionState): void {
    this.clearRetry()
    this.stopPing()
    const ws = this.ws
    this.ws = null
    this.everOpened = false
    if (ws) {
      ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null
      try {
        ws.close(1000)
      } catch {
        /* already closed */
      }
    }
    this.setState(state)
  }

  private send(msg: Record<string, unknown>): void {
    if (this.ws && this.ws.readyState === 1) this.rawSend(this.ws, msg)
  }

  private rawSend(ws: WebSocket, msg: Record<string, unknown>): void {
    try {
      ws.send(JSON.stringify(msg))
    } catch {
      /* the close handler will reconnect */
    }
  }

  private startPing(): void {
    this.stopPing()
    this.pingTimer = setInterval(() => this.send({ op: 'ping' }), this.opts.pingMs)
  }

  private stopPing(): void {
    if (this.pingTimer) clearInterval(this.pingTimer)
    this.pingTimer = null
  }

  private clearRetry(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer)
    this.retryTimer = null
  }

  private setState(state: ConnectionState): void {
    if (this.state === state) return
    this.state = state
    this.opts.onState?.(state)
  }
}

function safeCall(fn: () => void): void {
  try {
    fn()
  } catch (e) {
    if (import.meta.env.DEV) console.error('[realtime] handler failed', e)
  }
}

// ---- App singleton, React hooks and global handlers ----

type RealtimeStoreState = { state: ConnectionState }

/** Connection state for UI (for example a "Reconnecting" hint). */
export const useRealtimeStore = create<RealtimeStoreState>(() => ({ state: 'idle' }))

export const realtime = new RealtimeClient({ onState: (state) => useRealtimeStore.setState({ state }) })

export function setRealtimeAuth(loggedIn: boolean): void {
  realtime.setAuth(loggedIn)
}

export function useRealtimeState(): ConnectionState {
  return useRealtimeStore((s) => s.state)
}

/**
 * Subscribes while mounted. The handler can change on every render without resubscribing;
 * pass null or undefined as the channel to skip.
 *   useChannel(`post:${post.id}`, (event, data) => { if (event === 'comment.created') refetch() })
 */
export function useChannel(channel: string | null | undefined, handler: RealtimeHandler): void {
  const ref = useRef(handler)
  useEffect(() => {
    ref.current = handler
  })
  useEffect(() => {
    if (!channel) return
    return realtime.subscribe(channel, (event, data, ch) => ref.current(event, data, ch))
  }, [channel])
}

/** Keeps the viewer's own user channel open (unread badges, notifications, chat). */
export function useUserChannel(userId: string | null | undefined, handler?: RealtimeHandler): void {
  useChannel(userId ? `user:${userId}` : null, handler ?? noop)
}

function noop(): void {}

type UnreadCounts = { notifications: number; chats: number }
type MinimalQueryClient = {
  setQueryData: (key: readonly unknown[], updater: (old: unknown) => unknown) => unknown
  invalidateQueries: (filters: { queryKey: readonly unknown[] }, options?: { cancelRefetch?: boolean }) => Promise<void>
  getQueryData?: (key: readonly unknown[]) => unknown
}

function isUnread(data: unknown): data is UnreadCounts {
  if (!data || typeof data !== 'object') return false
  const d = data as Record<string, unknown>
  return typeof d.notifications === 'number' && typeof d.chats === 'number'
}

/**
 * Global behaviour, installed once in main.tsx:
 * unread.changed patches me.unread and invalidates ["me"]; a reconnect invalidates ["me"];
 * membership.updated (ban, removal, role, access) re reads every query on screen;
 * going online or showing the tab retries the socket right away.
 */
export function installGlobalRealtimeHandlers(qc: MinimalQueryClient, client: RealtimeClient = realtime): () => void {
  const offAny = client.onAny((event, data) => {
    if (event === 'membership.updated') {
      // E2E-J3-01: every open page (feed, post, lesson, calendar...) follows a ban, removal or lost plan at once, not
      // only the Members, Map and Leaderboards pages. Rare event for one person, so everything on screen re reads.
      void qc.invalidateQueries({ queryKey: [] })
      return
    }
    if (event !== 'unread.changed') return
    if (isUnread(data)) {
      qc.setQueryData(['me'], (old) =>
        old && typeof old === 'object' ? { ...(old as object), unread: { notifications: data.notifications, chats: data.chats } } : old,
      )
    }
    void qc.invalidateQueries({ queryKey: ['me'] })
  })
  // A read of me already in flight is kept, not cancelled: it answers with what the reconnect wants to know, and
  // cancelling it aborted its request for nothing (Sentry WEB-5, 2026-10-25).
  const offReconnect = client.onReconnect(() => void qc.invalidateQueries({ queryKey: ['me'] }, { cancelRefetch: false }))
  // E2E-J2-02: a session ended elsewhere (the socket closes with an auth code, or any call answers 401) re reads
  // the session and the community pages, so this device turns into the visitor view on its next click. Only while
  // the cache still holds a member, so a visitor's 401 never loops.
  // E2E-J4-06: one re check at a time. Each 401 that came in while me was being re read restarted that read
  // (invalidateQueries cancels a fetch in flight), so a page with signed in queries looped on the API forever.
  let checking = false
  const sessionEnded = () => {
    const me = qc.getQueryData?.(['me']) as { user?: unknown } | null | undefined
    if (!me?.user || checking) return
    checking = true
    void Promise.allSettled([qc.invalidateQueries({ queryKey: ['me'] }), qc.invalidateQueries({ queryKey: ['community'] })]).finally(() => {
      checking = false
    })
  }
  const offAuthLost = client.onAuthLost(sessionEnded)
  const offUnauthorized = onUnauthorized(sessionEnded)
  const retry = () => client.reconnectNow()
  const onVisible = () => {
    if (document.visibilityState === 'visible') client.reconnectNow()
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('online', retry)
    document.addEventListener('visibilitychange', onVisible)
  }
  return () => {
    offAny()
    offReconnect()
    offAuthLost()
    offUnauthorized()
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', retry)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }
}
